import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { colors } from "@styles/colors";
import {
  advanceFollow,
  createFollowState,
  isFollowIdle,
  pushSample,
  type FollowState,
  type LatLngPoint,
  type MarkerRenderState,
} from "./smoothing";
import { ARC_CURVATURE, ARC_SHADOW_CURVATURE, buildArcPath } from "./routeArc";
import {
  BASEMAP_ATTRIBUTION,
  BASEMAP_BACKGROUND,
  BASEMAP_MAX_ZOOM,
  BASEMAP_OPACITY,
  BASEMAP_TILE_URL,
} from "./basemap";
import { createBezierArc, type BezierArcLayer } from "./bezierArc";
import {
  DESTINATION_ICON_ANCHOR_Y,
  DESTINATION_ICON_SIZE,
  DRIVER_ICON_SIZE,
  destinationIconHtml,
  driverIconHtml,
  SUBPIXEL_CLASS,
  VEHICLE_CLASS,
} from "./driverIcons";

/**
 * 客戶端的配送追蹤地圖：畫面上只有「配送員」與「你家」兩個點。
 *
 * 刻意不畫完整路線，也不顯示其他停靠點——那些點就是其他客戶的住家位置。
 * 只有在客戶是下一站（isNextStop）時才連一條弧線，此時這條線不揭露任何第三人資訊。
 * 用弧線而不是直線的理由見 routeArc.ts：直線容易被讀成「他會直直開過來」。
 * 弧線下面墊一條壓扁的影子，兩端收合、中段落差最大——懸空感就是從這個落差來的。
 */

const driverIcon = () =>
  L.divIcon({
    className: "",
    iconSize: [DRIVER_ICON_SIZE, DRIVER_ICON_SIZE],
    iconAnchor: [DRIVER_ICON_SIZE / 2, DRIVER_ICON_SIZE / 2],
    html: driverIconHtml(),
  });

const destinationIcon = () =>
  L.divIcon({
    className: "",
    iconSize: [DESTINATION_ICON_SIZE, DESTINATION_ICON_SIZE],
    iconAnchor: [DESTINATION_ICON_SIZE / 2, DESTINATION_ICON_ANCHOR_Y],
    html: destinationIconHtml(),
  });

interface DriverMarkerProps {
  target: LatLngPoint;
  bearing?: number | null;
  ts?: number | null;
  faded: boolean;
  destination: LatLngPoint | null;
  showRouteHint: boolean;
  /** false = 不跑逐幀動畫，直接到位（例如這張地圖被全螢幕蓋住時） */
  animate: boolean;
}

/**
 * 配送員 marker 與提示弧線，含逐幀平滑。
 *
 * 逐幀的結果直接寫進 Leaflet 實例（setLatLng／改 transform），完全不經過 React state。
 * 之前是每幀 setState：一秒 60 次重繪整棵樹，其中 icon 物件每次都是新的，
 * react-leaflet 會據此呼叫 setIcon 把 marker 的 DOM 整個重建——在 LINE webview 裡就是明顯的頓。
 * 動畫本來就該只碰兩個屬性，不該是 React 的責任。
 *
 * 數學在 smoothing.ts（移植自後台 FleetMap 調好的參數）：收到的座標排成一條待走路徑，
 * 每一幀的速度由「還剩多少路 ÷ 還剩多少時間」決定。迴圈只在還有路要走時存在，
 * 走完就自己收掉——不是每收到一筆就重啟一段固定時長的插值。
 */
const DriverMarker: React.FC<DriverMarkerProps> = ({
  target,
  bearing,
  ts,
  faded,
  destination,
  showRouteHint,
  animate,
}) => {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);
  const arcRef = useRef<BezierArcLayer | null>(null);
  const shadowRef = useRef<BezierArcLayer | null>(null);
  const stateRef = useRef<FollowState | null>(null);
  const frameRef = useRef<number | null>(null);
  /** 上一幀的時間戳，用來算 dt（速度與角度的平滑都以 dt 正規化） */
  const lastFrameRef = useRef(0);
  /** icon 內部那兩層的快取：每幀 querySelector 是白花的 */
  const elsRef = useRef<{ root: HTMLElement; sub: HTMLElement | null; vehicle: HTMLElement | null } | null>(null);
  const icon = useMemo(() => driverIcon(), []);
  const destIcon = useMemo(() => destinationIcon(), []);
  // 同樣是參考比對：不記憶化的話每次 render 都會對目的地 marker 多做一次 setLatLng
  const destPos = useMemo<[number, number] | null>(
    () => (destination ? [destination.lat, destination.lng] : null),
    [destination?.lat, destination?.lng],
  );
  /**
   * marker 掛載時的位置。
   *
   * 這個陣列的「參考」必須每次 render 都一樣，不能只是值一樣：react-leaflet 判斷要不要
   * 呼叫 setLatLng 用的是 `props.position !== prevProps.position`，也就是參考比對。
   * 在 JSX 裡寫 position={[ref.current.lat, ref.current.lng]} 每次 render 都是新陣列，
   * 於是每收到一筆座標（＝一次 render）marker 就被拉回這個初始座標閃一下，
   * 下一幀動畫才把它接回去。凍住值沒有用，要凍住參考。
   *
   * 位置之後都由 rAF 直接寫進 Leaflet 實例，不再經過 React。
   */
  const initialRef = useRef(target);
  const initialPos = useMemo<[number, number]>(() => [initialRef.current.lat, initialRef.current.lng], []);
  /**
   * 弧線與影子：自己建圖層、自己收，不走 react-leaflet。
   * 它們每幀都要更新，交給 React 管等於每幀重建 props 與比對，沒有意義。
   */
  useEffect(() => {
    if (!showRouteHint || !destination) return;
    const from = stateRef.current?.position ?? initialRef.current;
    const shadow = createBezierArc(from, destination, ARC_SHADOW_CURVATURE, {
      color: colors.text,
      weight: 3.5,
      opacity: 0.12,
      lineCap: "round",
      // 影子不吃點擊，否則它會擋在地圖上方攔截手勢
      interactive: false,
    });
    const arc = createBezierArc(from, destination, ARC_CURVATURE, {
      color: colors.primary,
      weight: 3.5,
      opacity: 0.95,
      lineCap: "round",
      interactive: false,
    });
    // 影子先加才會在主弧下面：同一個 pane 內是照加入順序疊的
    shadow.addTo(map);
    arc.addTo(map);
    shadowRef.current = shadow;
    arcRef.current = arc;
    return () => {
      shadow.remove();
      arc.remove();
      shadowRef.current = null;
      arcRef.current = null;
    };
  }, [map, showRouteHint, destination?.lat, destination?.lng]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    /** icon 內部的兩層，換過 icon 才重查 */
    const resolveEls = () => {
      const root = marker.getElement() as HTMLElement | null;
      if (!root) return null;
      if (elsRef.current?.root !== root) {
        elsRef.current = {
          root,
          sub: root.querySelector<HTMLElement>(`.${SUBPIXEL_CLASS}`),
          vehicle: root.querySelector<HTMLElement>(`.${VEHICLE_CLASS}`),
        };
      }
      return elsRef.current;
    };

    /**
     * 縮放動畫期間，Leaflet 自己在管 marker 的位置：它在 zoomanim 事件裡用
     * _latLngToNewLayerPoint 把 marker 放到「目標視角」的座標上，再交給 CSS 過場。
     * 我們這邊用的是當下的座標基準，這時再寫一次 setLatLng 就是把它算好的蓋掉。
     */
    const isZooming = () => (map as unknown as { _animatingZoom?: boolean })._animatingZoom === true;

    const paint = (render: MarkerRenderState) => {
      if (isZooming()) return;
      marker.setLatLng([render.position.lat, render.position.lng]);
      const els = resolveEls();
      if (els?.vehicle) els.vehicle.style.transform = `rotate(${render.bearing}deg)`;
      if (els?.sub) {
        /**
         * Leaflet 剛剛把 marker 貼到整數像素上（Marker.update 是 latLngToLayerPoint().round()）。
         * 這裡把被丟掉的小數補回來，車才會是連續地滑而不是一格一格跳。
         * 餘數永遠在 [-0.5, 0.5]，所以不會和 Leaflet 的定位打架。
         */
        const p = map.project(L.latLng(render.position.lat, render.position.lng), map.getZoom());
        els.sub.style.transform = `translate3d(${p.x - Math.round(p.x)}px, ${p.y - Math.round(p.y)}px, 0)`;
      }
      if (!destination) return;
      arcRef.current?.setPoints(render.position, destination);
      shadowRef.current?.setPoints(render.position, destination);
    };

    // 第一次拿到座標就直接到位，不從地圖中心滑過去
    if (!stateRef.current) {
      stateRef.current = createFollowState(target, bearing ?? 0);
      paint(stateRef.current);
      return;
    }

    stateRef.current = pushSample(stateRef.current, target, ts, bearing);

    // 被全螢幕蓋住時不跑動畫，直接把路徑走完到最新位置
    if (!animate) {
      const state = stateRef.current;
      const last = state.pending.length ? state.pending[state.pending.length - 1] : state.position;
      stateRef.current = { ...state, position: last, pending: [], speedMps: 0 };
      paint(stateRef.current);
      return;
    }

    /**
     * 迴圈只在「還有路要走」時存在，走完就自己收掉，下一筆推送再開起來。
     * 不像固定時長的插值那樣每筆都重啟一段動畫——那正是「停頓 → 衝刺」的來源。
     */
    if (frameRef.current !== null) return;

    lastFrameRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      const dt = now - lastFrameRef.current;
      lastFrameRef.current = now;

      const state = advanceFollow(stateRef.current!, dt);
      stateRef.current = state;
      paint(state);

      if (isFollowIdle(state)) {
        frameRef.current = null;
        return;
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
  }, [target.lat, target.lng, bearing, ts, animate, map, destination?.lat, destination?.lng]);

  // 收掉動畫迴圈
  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    },
    [],
  );

  /** 位置過期時降透明。直接改 DOM，不重建 icon（重建等於 marker 閃一下）。 */
  useEffect(() => {
    const el = markerRef.current?.getElement();
    if (!el) return;
    el.style.opacity = faded ? "0.45" : "1";
  }, [faded]);

  return (
    <>
      {destination && <Marker position={destPos!} icon={destIcon} />}
      <Marker
        ref={markerRef}
        position={initialPos}
        icon={icon}
        zIndexOffset={500}
      />
    </>
  );
};

/**
 * 容器尺寸變了就通知 Leaflet 重新量。
 *
 * Leaflet 只在初始化時記住容器大小，之後容器變大它並不知道，多出來的那一塊不會鋪圖磚——
 * 畫面上就是地圖邊緣透出頁面底色，看起來像地圖沒填滿。這裡會踩到是因為滿版地圖的
 * 上緣偏移是量出來的：第一次算繪還沒有負 margin，量完才往上長高一個標題列的高度。
 */
const InvalidateOnResize: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

/**
 * 對焦上限。
 *
 * 原本是 16，但那讓最後 600 公尺完全不再放大——配送員一進到 600 公尺內，
 * 框住兩點所需的縮放就一直是 16，畫面從此定住，而那正是客戶最想看清楚的一段。
 * 17.5 可以一路跟到剩 200 公尺左右。再往上就沒意義了：兩個點近到只剩路口紋理，
 * 反而看不出彼此方位；圖磚層開了 detectRetina 之後實際上限也只到 18。
 */
const MAX_FIT_ZOOM = 17.5;

/**
 * 跟隨鏡頭：只在「真的需要」時動一次，其餘時間完全靜止。
 *
 * 先前試過讓它一直小步收攏，想做出「車越近地圖越近」的連續感。但實測下來，
 * 全螢幕（鏡頭完全不動）的車是順的、收合卡片（鏡頭一直在調）的不順——
 * 問題不在車，在鏡頭一直跟車搶畫面。在 Leaflet 上每一次調整還會連帶重建圖磚網格，
 * 一秒三次就是肉眼看到的頓。
 *
 * 所以改成：平常不動，只有這兩種情況才動一次，而且是一段完整的動畫。
 * 代價是放大變成偶爾發生的事，不是連續的；換來的是移動本身順。
 */
/** 兩次調整之間至少隔這麼久，避免邊緣來回觸發 */
const REFIT_MIN_INTERVAL_MS = 2_000;
/** 需要的縮放比現在緊這麼多級才值得重新框一次（＝這趟路確實短了一截） */
const REFIT_ZOOM_STEP = 1;
/**
 * 還沒有配送員座標、畫面上只有「你家」那一個點時用的縮放。
 * 這跟框兩個點是兩回事，不要共用 MAX_FIT_ZOOM——單點用到 17.5 會近到只看得到自家屋頂。
 */
const SINGLE_POINT_ZOOM = 16;

/**
 * Leaflet 執行時會讀 options.zoom 與 options.pan 這兩個巢狀選項
 * （setView 內部：_tryAnimatedZoom(center, zoom, options.zoom) 與 _tryAnimatedPan(center, options.pan)），
 * 但 @types/leaflet 的 FitBoundsOptions 沒有把它們列進去。這裡補上型別，不用 any。
 */
type AnimatedFitBoundsOptions = L.FitBoundsOptions & {
  zoom?: L.ZoomOptions;
  pan?: L.PanOptions;
};

/** 讓所有該看到的東西都留在畫面內。 */
const FitBounds: React.FC<{
  points: LatLngPoint[];
  /** 跟隨模式下要盯住的那一個點（配送員） */
  focus: LatLngPoint | null;
  mode: "follow" | "once";
  pad: FitPadding;
}> = ({ points, focus, mode, pad }) => {
  const map = useMap();
  const doneRef = useRef(false);
  /** 上次調整鏡頭的時間（見 REFIT_INTERVAL_MS） */
  const refitAtRef = useRef(0);
  // 用座標字串當相依：points 每次 render 都是新陣列，直接放進相依會每次 render 都重算視角。
  const key = points.map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join("|");
  useEffect(() => {
    if (points.length === 0) return;
    // once：全螢幕開啟時對一次焦就好。之後使用者可能自己平移過，再自動 fit 會把畫面搶回去。
    // 但這一次不能省——配送員可能在幾公里外，不對焦就只看得到自己家那個點。
    if (mode === "once" && doneRef.current) return;
    if (points.length === 1) {
      // 只有一個點（配送員座標還沒到）時先對到那個點，但不算「已對焦」——
      // 否則全螢幕開啟的瞬間只有你家的座標，一鎖定就再也不會把配送員帶進畫面。
      map.setView([points[0].lat, points[0].lng], SINGLE_POINT_ZOOM);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => L.latLng(p.lat, p.lng)));
    const paddingTopLeft = L.point(pad.left ?? 48, pad.top ?? 48);
    const paddingBottomRight = L.point(pad.right ?? 48, pad.bottom ?? 48);

    /**
     * 跟隨模式下要不要動畫面，只有兩個理由；其餘一律不動。
     * 每收到一筆座標就重對焦的話，整張地圖每一兩秒被推一次，路名、地標、弧線都會跟著晃。
     */
    if (mode === "follow" && doneRef.current && focus) {
      // 理由一：配送員快要離開畫面了（內縮 25% 當安全範圍）→ 立刻框回來，不等節拍
      const leaving = !map.getBounds().pad(-0.25).contains(L.latLng(focus.lat, focus.lng));
      const now = Date.now();
      if (now - refitAtRef.current < REFIT_MIN_INTERVAL_MS) return;
      if (!leaving) {
        // 理由二：這趟路短到可以整整多拉近一級了
        const fit = Math.min(MAX_FIT_ZOOM, map.getBoundsZoom(bounds, false, paddingTopLeft.add(paddingBottomRight)));
        if (fit - map.getZoom() < REFIT_ZOOM_STEP) return;
      }
      refitAtRef.current = Date.now();
    }

    doneRef.current = true;
    /**
     * zoom / pan 這兩個子選項都要明寫 animate: true，不能只寫最外層那個。
     *
     * setView 傳給 _tryAnimatedZoom 的是 options.zoom 而不是 options，所以最外層的
     * animate 根本到不了裡面。而它裡面有一道守門：
     *
     *   offset = 中心點位移 ÷ (1 - 1/scale)      // scale = 2^(縮放差)
     *   if (options.animate !== true && !地圖尺寸.contains(offset)) return false;
     *
     * 縮放步伐越小，(1 - 1/scale) 越接近 0，位移就被放得越大——0.05 級時是乘以 29 倍。
     * 只要重新置中偏了十幾個像素，算出來就超過一個螢幕，Leaflet 會放棄動畫改用硬切，
     * 畫面上就是一下一下地跳。明寫 animate: true 才會跳過這道守門。
     * pan 那條路徑（縮放沒變、只有平移）有一模一樣的判斷，所以一起給。
     */
    const fitOptions: AnimatedFitBoundsOptions = {
      paddingTopLeft,
      paddingBottomRight,
      maxZoom: MAX_FIT_ZOOM,
      animate: true,
      zoom: { animate: true },
      pan: { animate: true },
    };
    map.fitBounds(bounds, fitOptions);
  }, [map, mode, key, focus?.lat, focus?.lng, pad.top, pad.bottom, pad.left, pad.right]);
  return null;
};

/**
 * fitBounds 的邊距（像素）。標題列與資訊卡是浮在地圖上的，不留出對應的邊距，
 * marker 就會剛好躲在它們底下——畫面上看起來像地圖沒有配送員。
 */
export interface FitPadding {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

export interface TrackingMapProps {
  driver: LatLngPoint | null;
  bearing?: number | null;
  /** 這筆座標的回報時間（epoch ms）。跳點判定要看兩筆之間隔了多久，只有距離不夠。 */
  ts?: number | null;
  destination: LatLngPoint | null;
  /** 收合卡片一律 false（見下方註解）；只有全螢幕才開放操作 */
  interactive?: boolean;
  /** 位置過期時 marker 降透明，表示「這不是此刻的位置」 */
  faded?: boolean;
  /** 客戶是下一站時才連弧線——此時這條線不會揭露其他客戶的位置 */
  showRouteHint?: boolean;
  /** false = 這張地圖現在被蓋住，不必跑逐幀動畫（兩張地圖同時動是沒有意義的耗電與掉幀） */
  animate?: boolean;
  height: number | string;
  /** 浮在地圖上的元素（標題列、資訊卡）要讓出的空間 */
  fitPadding?: FitPadding;
}

const TrackingMap: React.FC<TrackingMapProps> = ({
  driver,
  bearing,
  ts,
  destination,
  interactive = false,
  faded = false,
  showRouteHint = false,
  animate = true,
  height,
  fitPadding,
}) => {
  /**
   * 對焦用的點。
   *
   * 取的是「剛收到的那一筆座標」而不是動畫中的位置：後者一秒變動六十次，
   * 收合態（follow）會變成每幀都重新 fitBounds，畫面看起來一直在抖。
   * 有弧線時連弧線一起算進去——弧頂會凸出兩點連線之外，只框兩個端點會被切掉。
   */
  const points = useMemo(() => {
    if (!driver) return destination ? [destination] : [];
    if (!destination) return [driver];
    return showRouteHint ? buildArcPath(driver, destination) : [driver, destination];
  }, [driver?.lat, driver?.lng, destination?.lat, destination?.lng, showRouteHint]);

  const center = driver ?? destination;
  if (!center) return null;

  return (
    <div
      className="lpg-tracking-map"
      style={{ height, width: "100%", overflow: "hidden", background: BASEMAP_BACKGROUND }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={15}
        /**
         * 允許小數縮放級數。預設 zoomSnap 是 1，任何對焦都會被吸附到整數級——
         * 跟隨鏡頭就只能一級一級跳，那不是「越來越近」，是「每隔一陣子跳一下」。
         * 代價是圖磚會被等比縮放（Leaflet 只會抓整數級的圖磚），畫面略軟；
         * 但 detectRetina 已經在抓高一級的圖磚，扣掉這層仍比原本清楚。
         */
        zoomSnap={0}
        style={{ height: "100%", width: "100%" }}
        attributionControl={interactive}
        zoomControl={false}
        /**
         * 收合卡片裡的地圖不吃手勢。這張卡包在訂單詳情的 ScrollView 裡，
         * 可拖曳的地圖會攔截垂直滑動，讓整頁滑不動——這是這種版型最常見的 bug。
         * 要互動請點展開鈕進全螢幕。
         */
        dragging={interactive}
        scrollWheelZoom={false}
        touchZoom={interactive}
        doubleClickZoom={interactive}
        keyboard={false}>
        <TileLayer
          url={BASEMAP_TILE_URL}
          attribution={BASEMAP_ATTRIBUTION}
          maxZoom={BASEMAP_MAX_ZOOM}
          opacity={BASEMAP_OPACITY}
          /**
           * 手機幾乎都是 2x／3x 螢幕，而國土測繪的圖磚只有 1x：直接鋪等於把 256px 的圖
           * 放大兩三倍，字和路網全糊掉（縮得越遠越明顯，因為那時畫面上全是細線與小字）。
           * detectRetina 會改抓下一級縮放的圖磚再縮排進同一格，等於雙倍解析度。
           * 代價是圖磚請求量變四倍，但 EMAP 圖磚很小，換來的清晰度值得。
           */
          detectRetina
        />
        <InvalidateOnResize />
        {/* 收合態持續跟隨；全螢幕只在開啟時對焦一次，之後把主導權交給使用者 */}
        <FitBounds
          points={points}
          focus={driver}
          mode={interactive ? "once" : "follow"}
          pad={fitPadding ?? {}}
        />
        {driver && (
          <DriverMarker
            target={driver}
            bearing={bearing}
            ts={ts}
            faded={faded}
            destination={destination}
            showRouteHint={showRouteHint}
            animate={animate}
          />
        )}
        {/* 配送員座標還沒到時，至少先把「你家」畫出來 */}
        {!driver && destination && <Marker position={[destination.lat, destination.lng]} icon={destinationIcon()} />}
      </MapContainer>
    </div>
  );
};

export default TrackingMap;
